import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Nav } from "../components/Nav";
import { Footer } from "../components/Footer";
import { api } from "../lib/api";
import { Check, Loader2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setStatus("paid");
          setBooking(res.data.booking);
          return;
        }
      } catch { /* keep polling */ }
      attempts += 1;
      if (attempts < 15) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="payment-success-page">
      <Nav />
      <main className="pt-40 pb-24 max-w-2xl mx-auto px-6">
        {status === "checking" && (
          <div data-testid="payment-checking" className="flex items-center gap-4 text-hearth-muted">
            <Loader2 className="w-6 h-6 animate-spin" />
            <p className="font-essay text-xl italic">Confirming with the bank…</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-confirmed">
            <Check className="w-10 h-10 text-hearth-ochre mb-6" strokeWidth={1.5} />
            <h1 className="font-display font-bold text-4xl text-hearth-ink">Locked in. Paid. Done.</h1>
            <p className="mt-6 font-essay text-xl text-hearth-ink/90 leading-loose">
              That's the whole admin. All that's left is the conversation.
            </p>
            {booking && (
              <div className="mt-8 border border-hearth-line bg-hearth-surface p-8 text-sm text-hearth-muted space-y-2" data-testid="booking-summary">
                <p><span className="text-hearth-ochre uppercase tracking-widest text-xs">Session</span><br />{booking.session_type?.replace(/_/g, " ")}</p>
                <p><span className="text-hearth-ochre uppercase tracking-widest text-xs">When</span><br />{booking.preferred_date} at {booking.time_slot}</p>
                <p><span className="text-hearth-ochre uppercase tracking-widest text-xs">Where</span><br />{booking.setting?.replace(/_/g, " ")}</p>
                {booking.karen_present && <p>Karen will be sitting in.</p>}
                {booking.companion && <p>You're bringing someone with you — good.</p>}
              </div>
            )}
            <Link to="/" data-testid="payment-home-link" className="mt-10 inline-block border border-hearth-line hover:border-hearth-ochre text-hearth-muted hover:text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
              Back to the Fire
            </Link>
          </div>
        )}
        {(status === "timeout" || status === "error") && (
          <div data-testid="payment-pending-note">
            <h1 className="font-display font-bold text-4xl text-hearth-ink">One moment.</h1>
            <p className="mt-6 font-essay text-xl text-hearth-ink/90 leading-loose">
              The payment went through but the confirmation is taking the long way round.
              If money left your card, your session is held — Matt will confirm personally.
            </p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  const [retrying, setRetrying] = useState(false);

  const retry = async () => {
    setRetrying(true);
    try {
      const res = await api.post("/payments/checkout", {
        booking_id: bookingId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      setRetrying(false);
    }
  };

  return (
    <div className="bg-hearth-bg min-h-screen" data-testid="payment-cancel-page">
      <Nav />
      <main className="pt-40 pb-24 max-w-2xl mx-auto px-6">
        <XCircle className="w-10 h-10 text-hearth-ember mb-6" strokeWidth={1.5} />
        <h1 className="font-display font-bold text-4xl text-hearth-ink">No harm done.</h1>
        <p className="mt-6 font-essay text-xl text-hearth-ink/90 leading-loose">
          The payment didn't go through, so nothing was charged. Your slot is held for a while yet.
        </p>
        {bookingId && (
          <button onClick={retry} disabled={retrying} data-testid="payment-retry-button" className="mt-10 inline-flex items-center gap-3 bg-hearth-ember hover:bg-hearth-ochre hover:text-hearth-bg disabled:opacity-30 text-hearth-ink text-xs font-semibold uppercase tracking-widest px-8 py-4 transition-colors">
            {retrying ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Try the Payment Again
          </button>
        )}
      </main>
      <Footer />
    </div>
  );
}
